import * as Diff from 'diff';
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

export const LANGUAGE_MAP: Record<string, { label: string; className: string }> = {
  go: { label: 'Go', className: 'bg-muted text-foreground border-border' },
  python: { label: 'Python', className: 'bg-muted text-foreground border-border' },
  java: { label: 'Java', className: 'bg-muted text-foreground border-border' },
  javascript: { label: 'JavaScript', className: 'bg-muted text-foreground border-border' },
  typescript: { label: 'TypeScript', className: 'bg-muted text-foreground border-border' },
  rust: { label: 'Rust', className: 'bg-muted text-foreground border-border' },
  ruby: { label: 'Ruby', className: 'bg-muted text-foreground border-border' },
};

export function getLanguageBadge(language: string) {
  const entry = LANGUAGE_MAP[language.toLowerCase()] || { label: language, className: 'bg-muted text-foreground border-border' };
  return <Badge className={entry.className}>{entry.label}</Badge>;
}

export interface DiffLine {
  line: string;
  type: 'unchanged' | 'removed' | 'added';
}

export function createDiff(oldText: string, newText: string): DiffLine[] {
  const changes = Diff.diffLines(oldText, newText);
  const result: DiffLine[] = [];

  changes.forEach(change => {
    const lines = change.value.split('\n');
    if (lines[lines.length - 1] === '') {
      lines.pop();
    }

    lines.forEach(line => {
      if (change.removed) {
        result.push({ line, type: 'removed' });
      } else if (change.added) {
        result.push({ line, type: 'added' });
      } else {
        result.push({ line, type: 'unchanged' });
      }
    });
  });

  return result;
}

interface DiffViewProps {
  oldText: string;
  newText: string;
  /** Width class for the gutter (e.g. "w-6" or "w-8") */
  gutterWidth?: string;
}

export function DiffView({ oldText, newText, gutterWidth = 'w-8' }: DiffViewProps) {
  const diffLines = createDiff(oldText, newText);
  return (
    <>
      {diffLines.map((diffLine, lineIndex) => (
        <div
          key={lineIndex}
          className={cn(
            "whitespace-pre-wrap flex",
            diffLine.type === 'removed' && "bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200",
            diffLine.type === 'added' && "bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200",
            diffLine.type === 'unchanged' && "text-foreground hover:bg-muted/50"
          )}
        >
          <span className={cn(
            `inline-block ${gutterWidth} text-right mr-3 text-muted-foreground select-none flex-shrink-0 border-r border-border/50 pr-2`,
            diffLine.type === 'removed' && "text-red-600 dark:text-red-400 bg-red-100/50 dark:bg-red-900/30",
            diffLine.type === 'added' && "text-green-600 dark:text-green-400 bg-green-100/50 dark:bg-green-900/30"
          )}>
            {diffLine.type === 'removed' ? '-' : diffLine.type === 'added' ? '+' : ' '}
          </span>
          <span className="flex-1 min-w-0">
            {diffLine.line}
          </span>
        </div>
      ))}
    </>
  );
}
